import { ScreenViewHistory } from "../shared/screen-shell/screen-view-history";
import { navigateBackOrFallback } from "../shared/screen-shell/screen-navigation.utils";
import { Component, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import { PlaySpace } from "../shared/models/playSpace";
import { PlaySpaceDeleteStatus, PlaySpaceMatchSummary, PlaySpaceService, PlaySpaceSummary } from "../shared/services/playspace.service";
import { PlayerService } from "../shared/services/player.service";
import { City } from "../shared/models/city";
import { Country } from "../shared/models/country";
import { Player } from "app/shared/models/player";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { BehaviorSubject, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';
import { DelayedSpinnerService } from "../shared/services/delayed-spinner.service";
import { environment } from "app/environments/environment";
import { TranslateService } from '@ngx-translate/core';
import { ChatService } from "../shared/services/chat.service";
import { Event, EventRecurringType } from "../shared/models/event";
import { EventService } from "../shared/services/event.service";
import { PlayerEventInfo } from "../shared/models/playerEventInfo";
import { DialogService } from "../core/dialog/dialog.service";

const PLACE_AUTOCOMPLETE_MIN_QUERY_LENGTH = 3;

@Component({
    selector: "play-space",
    templateUrl: "./play-space.component.html",
    styleUrls: ["./play-space.component.css"],
    standalone: false
})
export class PlaySpaceFormComponent implements OnInit, OnDestroy {
  @Input()
  public playSpace: PlaySpace;

  public editMode: boolean = false;
  public detailMode: boolean = false;
  public editSection: "menu" | "all" | "basic" | "address" | "contact" | "hours" | "delete" = "all";
  public deleteStatus: PlaySpaceDeleteStatus = null;
  public deleteStatusLoading: boolean = false;
  public playSpaceSummary: PlaySpaceSummary = null;
  public playSpaceSummaryLoading: boolean = false;
  public currentLocationLatitude: number = 44.78;
  public currentLocationLongitude: number = 20.44;

  public zoom: number = 11;

  public markers: PlaySpace[] = Array<PlaySpace>();
  public activeView: "list" | "map" | "mine" = "list";
  public locationSearch: string = "";
  public selectedPlaySpace: PlaySpace = null;
  public loading = false;
  public readonly playSpacesReady$ = new BehaviorSubject<boolean>(false);
  public currentPlayer: Player;
  public selectedAddress: string = null;
  public addressSuggestions: string[] = [];
  public addressDropdownOpen = false;
  public addressLoading = false;
  public creatingMatchAtLocationId: string = null;

  private creatingNewPlayspaceFromEvent: boolean = false;
  private creatingDraft = false;
  private readonly addressQuery$ = new Subject<string>();
  private readonly addressSubscriptions = new Subscription();
  mapOptions: google.maps.MapOptions;
  detailMapOptions: google.maps.MapOptions;
  clickableAdvancedMarkerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
    gmpClickable: true
  };
  private readonly boundPlaySpaceMarkers = new WeakSet<google.maps.marker.AdvancedMarkerElement>();

  constructor(
    public playSpaceService: PlaySpaceService,
    public route: ActivatedRoute,
    public playerService: PlayerService,
    public router: Router,
    private _location: Location,
    private spinner: DelayedSpinnerService,
    private translate: TranslateService,
    private chatService: ChatService,
    private eventService: EventService,
    private zone: NgZone,
    private dialogService: DialogService
  ) {
  }

  ngOnInit() {
    this.loading = true;
    this.spinner.show();
    this.currentPlayer = this.playerService.getCurrentPlayer();
    this.playerService.checkCity();
    this.setupAddressAutocomplete();

    this.creatingNewPlayspaceFromEvent = JSON.parse(this.route.snapshot.params["createNewPlayspace"]);

    const prepared = this.route.snapshot.data['screen'];
    if (prepared) {
      const coordinates = this.currentPlayerCoordinates();
      if (coordinates) {
        this.currentLocationLatitude = coordinates.latitude;
        this.currentLocationLongitude = coordinates.longitude;
      }
      this.updateMapOptions(this.currentLocationLatitude, this.currentLocationLongitude, this.zoom);
      this.markers = prepared.items || [];
      this.markers.forEach(item => {
        item.viewedByManager = this.isManagedByCurrentPlayer(item);
        this.preparePlaySpaceForMap(item);
      });
      if (this.creatingNewPlayspaceFromEvent) this.editCreatePlayspace(null);
      else if (prepared.selected) this.showPreparedDetail(prepared.selected, prepared.summary);
      this.finishInitialLoad();
      return;
    }

    const currentCoordinates = this.currentPlayerCoordinates();
    if (!this.canLoadLocations()) {
      this.updateMapOptions(this.currentLocationLatitude, this.currentLocationLongitude, this.zoom);

      if (this.creatingNewPlayspaceFromEvent) {
        this.editCreatePlayspace(null);
      } else {
        this.openInitialPlaySpaceFromRoute();
      }

      this.finishInitialLoad();
      return;
    }

    this.currentLocationLatitude = currentCoordinates.latitude;
    this.currentLocationLongitude = currentCoordinates.longitude;
    this.updateMapOptions(this.currentLocationLatitude, this.currentLocationLongitude, this.zoom);

    this.playSpaceService.getCurrentLocations().subscribe((playspaces) => {
      for (let playspace of playspaces) {
        if (this.isManagedByCurrentPlayer(playspace)) {
          playspace.viewedByManager = true;
        }

        this.preparePlaySpaceForMap(playspace);
        this.markers.push(playspace);
      }

      if (this.creatingNewPlayspaceFromEvent) {
        this.editCreatePlayspace(null);
      }

      this.finishInitialLoad();
    }, () => this.finishInitialLoad());
  }

  ngOnDestroy(): void {
    this.viewHistory?.destroy();
    this.addressSubscriptions.unsubscribe();
  }

  private setupAddressAutocomplete(): void {
    this.addressSubscriptions.add(this.addressQuery$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => {
        const partialAddress = query ? query.trim() : "";
        if (partialAddress.length < PLACE_AUTOCOMPLETE_MIN_QUERY_LENGTH || !this.hasPlaySpaceCity()) {
          this.addressLoading = false;
          return of([]);
        }

        this.addressLoading = true;
        return this.getAddressAsObservable(partialAddress).pipe(
          catchError(() => of([])),
          finalize(() => this.addressLoading = false)
        );
      })
    ).subscribe((addresses) => {
      this.addressSuggestions = addresses || [];
      this.addressDropdownOpen = this.editMode && (this.editSection === "all" || this.editSection === "address") && this.addressSuggestions.length > 0;
    }));
  }

  private viewHistory: ScreenViewHistory<any>;
  private captureLocationView(): any {
    return {editMode: this.editMode, detailMode: this.detailMode, editSection: this.editSection,
      selectedPlaySpace: this.selectedPlaySpace, playSpace: this.playSpace,
      playSpaceSummary: this.playSpaceSummary, activeView: this.activeView, locationSearch: this.locationSearch};
  }

  private finishInitialLoad(): void {
    if (!this.viewHistory) this.viewHistory = new ScreenViewHistory(this._location, this.router,
      `${this.currentPlayer.id}:locations`, () => this.captureLocationView(), value => {
        Object.assign(this, value);
        // History stores the view, not an older copy of successfully saved location data.
        if (this.playSpace?.id) this.playSpace = this.markers.find(item => item.id === this.playSpace.id) || this.playSpace;
        if (this.selectedPlaySpace?.id) this.selectedPlaySpace = this.markers.find(item => item.id === this.selectedPlaySpace.id) || this.selectedPlaySpace;
        this.syncSelectedAddressFromPlaySpace();
        if (this.selectedPlaySpace) this.detailMapOptions = this.buildPlaySpaceMapOptions(this.selectedPlaySpace);
      });
    this.spinner.hide();
    this.loading = false;
    this.playSpacesReady$.next(true);
  }

  private openInitialPlaySpaceFromRoute(): void {
    const playSpaceId = this.route.snapshot.queryParamMap.get("playSpaceId");
    if (!playSpaceId) {
      return;
    }

    const playSpace = this.markers.find((marker) => marker && marker.id === playSpaceId);
    if (playSpace) {
      this.openPlaySpaceDetail(playSpace);
    }
  }

  private updateMapOptions(latitude: number, longitude: number, zoom: number): void {
    this.mapOptions = {
      mapId: environment.GOOGLE_MAPS_MAP_ID,
      center: {lat: latitude, lng: longitude},
      mapTypeControl: false,
      zoom: zoom
    };
  }

  public getAddressAsObservable(partialAddres: string) {
    return this.playerService.getAddress(partialAddres, this.playSpace.latitude, this.playSpace.longitude);
  }

  public getCitiesAsObservable(partialCityName: string) {
    return this.playerService.getCities(partialCityName);
  }

  public typeaheadCitySelected(selectedCity: any) {
    this.playSpace.city.name = selectedCity.value;
    this.playerService.getLatAndLng(this.playSpace.city.name).subscribe((latLng) => {
      this.playSpace.latitude = latLng.lat;
      this.playSpace.longitude = latLng.lng;
      this.playSpace.googleLatLng = new google.maps.LatLng(this.playSpace.latitude, this.playSpace.longitude);
    });
  }

  public typeaheadAddressSelected(selectedAddress: any) {
    this.playSpace.address = selectedAddress.value;
    this.playerService.getLatAndLng(this.playSpace.city.name + " " + selectedAddress.value).subscribe((latLng) => {
      this.playSpace.latitude = latLng.lat;
      this.playSpace.longitude = latLng.lng;
      this.playSpace.googleLatLng = new google.maps.LatLng(this.playSpace.latitude, this.playSpace.longitude);
    });
  }

  public onAddressInput(value: string): void {
    this.playSpace.address = value || "";
    if (this.selectedAddress !== this.playSpace.address) {
      this.selectedAddress = null;
    }
    this.addressQuery$.next(this.playSpace.address);
  }

  public selectAddress(address: string): void {
    this.playSpace.address = address;
    this.selectedAddress = address;
    this.addressSuggestions = [];
    this.addressDropdownOpen = false;
    this.playerService.getLatAndLng(this.playSpaceCityName() + " " + address).subscribe((latLng) => {
      this.playSpace.latitude = latLng.lat;
      this.playSpace.longitude = latLng.lng;
      this.playSpace.googleLatLng = new google.maps.LatLng(this.playSpace.latitude, this.playSpace.longitude);
      this.mapOptions = Object.assign({}, this.mapOptions, {
        center: {lat: this.playSpace.latitude, lng: this.playSpace.longitude},
        zoom: Math.max(this.mapOptions && this.mapOptions.zoom ? Number(this.mapOptions.zoom) : this.zoom, 15)
      });
    });
  }

  public showAddressSuggestions(): void {
    this.addressDropdownOpen = this.addressSuggestions.length > 0;
  }

  public closeAddressSuggestionsLater(): void {
    window.setTimeout(() => this.addressDropdownOpen = false, 150);
  }

  public isAddressValidForSave(): boolean {
    if (!this.playSpace || !this.playSpace.address) {
      return true;
    }
    return this.selectedAddress === this.playSpace.address;
  }

  public locationsShellContentClass(): string {
    return "";
  }

  public showLocationsShellHeader(): boolean {
    return this.editMode || (!!this.detailMode && !!this.selectedPlaySpace);
  }

  public locationsShellTitle(): string {
    if (!this.editMode && this.detailMode && this.selectedPlaySpace) {
      return this.selectedPlaySpace.name || "";
    }

    return "";
  }

  public locationsShellTitleKey(): string {
    if (!this.editMode) {
      return null;
    }

    return this.playSpace && this.playSpace.id ? "locations.editTitle" : "locations.new";
  }

  public locationsShellBackLabelKey(): string {
    return this.editMode ? "common.cancel" : "common.back";
  }

  public handleLocationsShellBack(): void {
    if (this.editMode) {
      this.cancelPlaySpaceEdit();
      return;
    }

    if (this.detailMode && this.selectedPlaySpace) {
      this.closePlaySpaceDetail();
      return;
    }

    this.goBack();
  }

  public editCreatePlayspace(playSpace: PlaySpace): void {
    if (this.creatingDraft) return;
    this.viewHistory?.push({...this.captureLocationView(), editMode: true, editSection: "menu", playSpace, selectedPlaySpace: playSpace});
    // A new location is a server-backed draft. Keep the source visible until it exists.
    this.editMode = !!playSpace;
    this.resetScreenScroll();

    if (this.isManagedByCurrentPlayer(playSpace)) {
      playSpace.viewedByManager = true;
    }

    if (playSpace) {
      this.playSpace = playSpace;
      this.selectedPlaySpace = playSpace;
      this.detailMode = true;
      this.editSection = "menu";
      this.playSpace.googleLatLng = new google.maps.LatLng(this.playSpace.latitude, this.playSpace.longitude);
      this.syncSelectedAddressFromPlaySpace();
    } else {
      this.creatingDraft = true;
      this.createDefaultPlaySpace();
    }
  }

  private createDefaultPlaySpace(): void {
    this.selectedPlaySpace = null;
    this.detailMode = false;
    this.editSection = "menu";
    this.playSpace = this.buildDefaultPlaySpace();
    this.syncSelectedAddressFromPlaySpace();

    const cityName = this.playSpace && this.playSpace.city && this.playSpace.city.name ? this.playSpace.city.name.trim() : "";
    if (!cityName) {
      this.saveNewPlaySpaceDraft();
      return;
    }

    this.spinner.show();
    this.loading = true;
    this.playerService.getLatAndLng(cityName).subscribe(
      (latLng) => {
        this.playSpace.latitude = latLng.lat;
        this.playSpace.longitude = latLng.lng;
        this.playSpace.googleLatLng = new google.maps.LatLng(this.playSpace.latitude, this.playSpace.longitude);
        this.saveNewPlaySpaceDraft();
      },
      () => this.saveNewPlaySpaceDraft()
    );
  }

  private buildDefaultPlaySpace(): PlaySpace {
    const draft = new PlaySpace();
    const currentCoordinates = this.currentPlayerCoordinates();
    const cityName = this.currentPlayer && this.currentPlayer.city ? this.currentPlayer.city.trim() : "";

    draft.name = this.translate.instant("locations.defaultName");
    draft.latitude = currentCoordinates ? currentCoordinates.latitude : this.currentLocationLatitude;
    draft.longitude = currentCoordinates ? currentCoordinates.longitude : this.currentLocationLongitude;
    draft.googleLatLng = new google.maps.LatLng(draft.latitude, draft.longitude);
    draft.city = new City();
    draft.city.name = cityName;
    draft.city.country = new Country();
    draft.address = "";
    draft.manager = this.currentPlayer;
    draft.viewedByManager = true;

    return draft;
  }

  private saveNewPlaySpaceDraft(): void {
    this.playSpace.location = null;
    this.spinner.show();
    this.loading = true;

    this.playSpaceService.createPlaySpace(this.playSpace).subscribe(
      (playSpace) => this.handlePlaySpaceCreated(playSpace, false),
      (error) => {
        console.log(error);
        this.editSection = "all";
        this.editMode = true;
        this.creatingDraft = false;
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public mapUpdatePlayLocationInfo(m: PlaySpace, $event: any): void {
    console.log($event)
    if (this.editMode && this.playSpace) {
      this.playSpace.latitude = Number($event.latLng.lat().toFixed(5));
      this.playSpace.longitude = Number($event.latLng.lng().toFixed(5));
      this.playSpace.googleLatLng = new google.maps.LatLng(this.playSpace.latitude, this.playSpace.longitude);
      this.selectedAddress = this.playSpace.address || null;
    }
  }

  // public mapReadyHandler(map: google.maps.Map): void {
  //   this.map = map;
  //   this.mapClickListener = this.map.addListener("click", (e: google.maps.MouseEvent) => {
  //     this.zone.run(() => {
  //       if (this.editMode && this.playSpace) {
  //         this.playSpace.latitude = Number(e.latLng.lat().toFixed(5));
  //         this.playSpace.longitude = Number(e.latLng.lng().toFixed(5));
  //       }
  //     });
  //   });
  // }

  public locationSaved = false;

  public onSubmit(): void {
    if (this.loading) return;
    this.locationSaved = false;
    this.playSpace.location = null;
    const completingIncompleteSetup = this.showIncompleteSetupNotice(this.playSpace);
    if (completingIncompleteSetup) {
      this.playSpace.incompleteSetup = false;
    }
    this.spinner.show();
    this.loading = true;

    if (this.playSpace.id) {
      this.playSpaceService.updatePlaySpace(this.playSpace).subscribe(
        (playSpace) => {
          this.applySavedPlaySpace(playSpace);
          this.locationSaved = true;

          this.spinner.hide();
          this.loading = false;
        },
        (error) => {
          if (completingIncompleteSetup) {
            this.playSpace.incompleteSetup = true;
          }
          console.log(error);
          this.spinner.hide();
          this.loading = false;
        }
      );
    } else {
      this.playSpaceService.createPlaySpace(this.playSpace).subscribe(
        (playSpace) => {
          this.handlePlaySpaceCreated(playSpace, true);
        },
        (error) => {
          if (completingIncompleteSetup) {
            this.playSpace.incompleteSetup = true;
          }
          console.log(error);
          this.spinner.hide();
          this.loading = false;
        }
      );
    }
  }

  public showIncompleteSetupNotice(playSpace: PlaySpace): boolean {
    return this.isManagedByCurrentPlayer(playSpace) && !!playSpace && playSpace.incompleteSetup === true;
  }

  public saveIncompleteSetupLocation(playSpace: PlaySpace): void {
    if (!this.showIncompleteSetupNotice(playSpace) || this.loading) {
      return;
    }

    const locationForUpdate = Object.assign(new PlaySpace(), playSpace);
    locationForUpdate.location = null;
    locationForUpdate.incompleteSetup = false;

    this.spinner.show();
    this.loading = true;
    this.playSpaceService.updatePlaySpace(locationForUpdate).subscribe(
      (savedPlaySpace) => {
        this.applySavedPlaySpace(savedPlaySpace, false);
        this.spinner.hide();
        this.loading = false;
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.loading = false;
      }
    );
  }

  public cancelIncompleteSetupLocation(playSpace: PlaySpace): void {
    if (!this.showIncompleteSetupNotice(playSpace) || this.loading) {
      return;
    }

    if (!playSpace.id) {
      this.clearIncompleteSetupLocation(playSpace);
      return;
    }

    this.spinner.show();
    this.loading = true;
    this.playSpaceService.removePlaySpace(playSpace.id).subscribe(
      () => {
        this.clearIncompleteSetupLocation(playSpace);
        this.spinner.hide();
        this.loading = false;
      },
      (error) => {
        console.log(error);
        this.spinner.hide();
        this.loading = false;
        this.dialogService.alert({
          title: this.translate.instant('common.alert'),
          text: this.getBackendErrorMessage(error, this.translate.instant('locations.deleteFailed'))
        });
      }
    );
  }

  private clearIncompleteSetupLocation(playSpace: PlaySpace): void {
    const playSpaceId = playSpace && playSpace.id ? playSpace.id : null;
    if (playSpaceId) {
      this.markers = this.markers.filter((marker) => marker.id !== playSpaceId);
    }
    if (this.selectedPlaySpace && (!playSpaceId || this.selectedPlaySpace.id === playSpaceId)) {
      this.selectedPlaySpace = null;
    }
    if (this.playSpace && (!playSpaceId || this.playSpace.id === playSpaceId)) {
      this.playSpace = null;
    }
    this.editMode = false;
    this.detailMode = false;
    this.editSection = "menu";

    if (this.creatingNewPlayspaceFromEvent) {
      sessionStorage.setItem("returningFromNewPlayspaceForm", "true");
      sessionStorage.removeItem("newPlayspace");
      this.navigateBackToCallingMatchScreen();
    }
  }

  private applySavedPlaySpace(savedPlaySpace: PlaySpace, incompleteSetup: boolean = this.playSpace ? this.playSpace.incompleteSetup === true : false): void {
    this.playSpace = Object.assign(new PlaySpace(), savedPlaySpace);
    this.playSpace.incompleteSetup = incompleteSetup;
    this.playSpace.viewedByManager = true;
    this.preparePlaySpaceForMap(this.playSpace);
    this.markers = this.markers.map((marker) => marker.id === this.playSpace.id ? this.playSpace : marker);
    this.selectedPlaySpace = this.playSpace;
    this.detailMapOptions = this.buildPlaySpaceMapOptions(this.playSpace);
    this.detailMode = true;
    this.syncSelectedAddressFromPlaySpace();
  }

  private handlePlaySpaceCreated(playSpace: PlaySpace, returnToCallingMatch: boolean): void {
    this.creatingDraft = false;
    this.playSpace = playSpace;
    this.playSpace.viewedByManager = true;

    this.preparePlaySpaceForMap(this.playSpace);
    this.markers = this.markers.filter((marker) => marker.id !== this.playSpace.id);
    this.markers.push(this.playSpace);
    this.selectedPlaySpace = this.playSpace;
    this.detailMapOptions = this.buildPlaySpaceMapOptions(this.playSpace);
    this.detailMode = true;
    this.editSection = "menu";
    this.syncSelectedAddressFromPlaySpace();
    this.spinner.hide();
    this.loading = false;

    if (returnToCallingMatch && this.creatingNewPlayspaceFromEvent) {
      sessionStorage.setItem("returningFromNewPlayspaceForm", "true");
      sessionStorage.setItem("newPlayspace", JSON.stringify(this.playSpace));
    }

    this.editMode = true;
  }

  public deletePlayspace(): void {
    if (this.deleteStatus && !this.deleteStatus.deletable) {
      return;
    }

    this.playSpaceService.removePlaySpace(this.playSpace.id).subscribe(
      (response) => {
        //this.goBack();
        this.editMode = false;
        this.markers = this.markers.filter(item => item.id !== this.playSpace.id);
        this.selectedPlaySpace = null;
        this.detailMode = false;
      },
      (error) => {
        console.log(error);
          this.dialogService.alert({
            title: this.translate.instant('common.alert'),
            text: this.getBackendErrorMessage(error, this.translate.instant('locations.deleteFailed'))
          });
          
      }
    );
  }

  private getBackendErrorMessage(error: any, fallback: string): string {
    if (error && error.error) {
      if (typeof error.error === "string") {
        return error.error;
      }
      if (error.error.message) {
        return error.error.message;
      }
      if (error.error.error) {
        return error.error.error;
      }
    }
    return fallback;
  }

  public isManagedByCurrentPlayer(playSpace: PlaySpace): boolean {
    return !!playSpace
      && !!playSpace.manager
      && !!this.currentPlayer
      && !!this.currentPlayer.id
      && playSpace.manager.id === this.currentPlayer.id;
  }

  public playSpaceManagerName(): string {
    return this.creatorNickName(this.playSpace);
  }

  public cancelPlaySpaceEdit(): void {
    if (this.viewHistory?.back()) return;
    if (this.editMode && this.editSection !== "menu" && this.playSpace && this.playSpace.id) {
      this.editSection = "menu";
      return;
    }

    this.editMode = false;

    if (this.creatingNewPlayspaceFromEvent) {
      sessionStorage.setItem("returningFromNewPlayspaceForm", "true");
      if (this.playSpace && this.playSpace.id) {
        sessionStorage.setItem("newPlayspace", JSON.stringify(this.playSpace));
      } else {
        sessionStorage.removeItem("newPlayspace");
      }

      this.navigateBackToCallingMatchScreen();
      return;
    }

    if (!this.playSpace || !this.playSpace.id) {
      this.detailMode = false;
      return;
    }

    this.selectedPlaySpace = this.playSpace;
  }

  public openEditSection(section: "basic" | "address" | "contact" | "hours"): void {
    this.locationSaved = false;
    this.viewHistory?.push({...this.captureLocationView(), editSection: section});
    this.editSection = section;
    if (section === "address") {
      this.syncSelectedAddressFromPlaySpace();
    }
    this.resetScreenScroll();
  }

  public openDeleteSection(): void {
    this.editSection = "delete";
    this.deleteStatus = null;
    this.deleteStatusLoading = true;
    this.resetScreenScroll();
    this.loadDeleteStatus();
  }

  private loadDeleteStatus(): void {
    if (!this.playSpace || !this.playSpace.id) {
      this.deleteStatus = null;
      this.deleteStatusLoading = false;
      return;
    }
    this.playSpaceService.getDeleteStatus(this.playSpace.id).subscribe(
      (status) => {
        this.deleteStatus = status;
        this.deleteStatusLoading = false;
      },
      () => {
        this.deleteStatus = null;
        this.deleteStatusLoading = false;
      }
    );
  }

  private navigateBackToCallingMatchScreen(): void {
    const returnRoute = sessionStorage.getItem("playspaceReturnRoute");
    if (returnRoute) {
      navigateBackOrFallback(this._location, this.router, [returnRoute]);
      return;
    }

    this.router.navigate(["/event-detail", true]);
  }

  public goBack(): void {
    //console.log('uh');
    navigateBackOrFallback(this._location, this.router, ["/event-list"]);
  }

  public setActiveView(view: "list" | "map" | "mine"): void {
    this.activeView = view;
    this.detailMode = false;
    if (view === "mine" && this.selectedPlaySpace && !this.isManagedByCurrentPlayer(this.selectedPlaySpace)) {
      this.selectedPlaySpace = null;
    }
  }

  public visiblePlaySpaces(): PlaySpace[] {
    const query = (this.locationSearch || "").trim().toLowerCase();
    return (this.markers || [])
      .filter((playSpace) => this.activeView !== "mine" || this.isManagedByCurrentPlayer(playSpace))
      .filter((playSpace) => {
        if (!query) {
          return true;
        }
        return [
          playSpace.name,
          playSpace.address,
          playSpace.city && playSpace.city.name,
          playSpace.city && playSpace.city.country && playSpace.city.country.name
        ].filter(Boolean).join(" ").toLowerCase().indexOf(query) !== -1;
      })
      .sort((a, b) => {
        if (this.isManagedByCurrentPlayer(a) !== this.isManagedByCurrentPlayer(b)) {
          return this.isManagedByCurrentPlayer(a) ? -1 : 1;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
  }

  public locatedVisiblePlaySpaces(): PlaySpace[] {
    return this.visiblePlaySpaces().filter((playSpace) => !!playSpace.googleLatLng);
  }

  public bindPlaySpaceMarkerClick(advancedMarker: google.maps.marker.AdvancedMarkerElement, playSpace: PlaySpace): void {
    if (!advancedMarker || this.boundPlaySpaceMarkers.has(advancedMarker)) {
      return;
    }

    this.boundPlaySpaceMarkers.add(advancedMarker);
    advancedMarker.addEventListener("gmp-click", () => {
      this.zone.run(() => this.openPlaySpaceDetail(playSpace));
    });
  }

  public selectPlaySpace(playSpace: PlaySpace): void {
    this.selectedPlaySpace = playSpace;
    const coordinates = this.playSpaceCoordinates(playSpace);
    if (coordinates) {
      this.mapOptions = Object.assign({}, this.mapOptions, {
        center: {lat: coordinates.latitude, lng: coordinates.longitude},
        zoom: Math.max(this.mapOptions && this.mapOptions.zoom ? Number(this.mapOptions.zoom) : this.zoom, 14)
      });
    }
  }

  public openPlaySpaceDetail(playSpace: PlaySpace): void {
    if (!playSpace?.id || this.playSpaceSummaryLoading) return;
    this.playSpaceSummaryLoading = true;
    this.spinner.show();
    this.addressSubscriptions.add(this.playSpaceService.getSummary(playSpace.id).pipe(
      finalize(() => { this.playSpaceSummaryLoading = false; this.spinner.hide(); })
    ).subscribe(summary => this.showPreparedDetail(playSpace, summary), () => {
      this.dialogService.alert({title: this.translate.instant('common.alert'), text: this.translate.instant('errors.generic')});
    }));
  }

  private showPreparedDetail(playSpace: PlaySpace, summary: PlaySpaceSummary): void {
    this.viewHistory?.push({...this.captureLocationView(), selectedPlaySpace: playSpace, playSpaceSummary: summary, detailMode: true});
    this.selectPlaySpace(playSpace);
    this.playSpaceSummary = summary;
    this.detailMapOptions = this.buildPlaySpaceMapOptions(playSpace);
    this.detailMode = true;
    this.resetScreenScroll();
  }

  public closePlaySpaceDetail(): void {
    if (this.viewHistory?.back()) return;
    this.goBack();
  }

  private resetScreenScroll(): void {
    window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 0);
  }

  public hasPlaySpaceCity(): boolean {
    return !!this.playSpaceCityName();
  }

  public playSpaceCityName(): string {
    return this.playSpace && this.playSpace.city && this.playSpace.city.name ? this.playSpace.city.name.trim() : "";
  }

  private syncSelectedAddressFromPlaySpace(): void {
    this.selectedAddress = this.playSpace && this.playSpace.address ? this.playSpace.address : null;
    this.addressSuggestions = [];
    this.addressDropdownOpen = false;
  }

  private buildPlaySpaceMapOptions(playSpace: PlaySpace): google.maps.MapOptions {
    const coordinates = this.playSpaceCoordinates(playSpace);
    if (!coordinates) {
      return this.mapOptions;
    }

    return {
      mapId: environment.GOOGLE_MAPS_MAP_ID,
      center: {lat: coordinates.latitude, lng: coordinates.longitude},
      mapTypeControl: false,
      zoom: 15
    };
  }

  public displayValue(value: string): string {
    return value && value.trim() ? value : "-";
  }

  public hasText(value: string): boolean {
    return !!(value && value.trim());
  }

  public hasLocationDetails(playSpace: PlaySpace): boolean {
    return !!playSpace && (
      this.hasText(playSpace.phone)
      || this.hasText(playSpace.workingHours)
      || this.hasText(this.creatorNickName(playSpace))
    );
  }

  public creatorNickName(playSpace: PlaySpace): string {
    return playSpace && playSpace.manager && this.hasText(playSpace.manager.nickName)
      ? playSpace.manager.nickName.trim()
      : "";
  }

  public callPhone(phone: string): void {
    if (!this.hasText(phone)) {
      return;
    }
    window.location.href = "tel:" + phone.replace(/[^+\d]/g, "");
  }

  public canChatWithCreator(playSpace: PlaySpace): boolean {
    return !!(playSpace
      && playSpace.manager
      && playSpace.manager.id
      && playSpace.manager.openProfile
      && (!this.currentPlayer || playSpace.manager.id !== this.currentPlayer.id));
  }

  public chatWithCreator(playSpace: PlaySpace): void {
    if (!this.canChatWithCreator(playSpace)) {
      return;
    }
    this.chatService.openDirectChat(playSpace.manager.id).subscribe(thread => {
      if (thread && thread.chat && thread.chat.id) {
        this.router.navigate(["/messages", { chatId: thread.chat.id }]);
      }
    });
  }

  public openLocationMatch(match: PlaySpaceMatchSummary): void {
    if (!match || !match.id) {
      return;
    }
    this.router.navigate(["/event-players", false, { eventId: match.id }]);
  }

  public createMatchAtLocation(playSpace: PlaySpace): void {
    if (!playSpace || !playSpace.id || !this.currentPlayer || this.creatingMatchAtLocationId) {
      return;
    }

    this.dialogService.alert({
      title: this.translate.instant("events.createTeamConfirmTitle"),
      text: this.translate.instant("events.createTeamAtLocationConfirmMessage", {
        location: playSpace.name || this.translate.instant("common.location")
      }),
      closeLabel: this.translate.instant("common.cancel"),
      actionLabel: this.translate.instant("events.createTeamConfirmAction"),
      action: () => this.createMatchAtLocationConfirmed(playSpace)
    });
  }

  private createMatchAtLocationConfirmed(playSpace: PlaySpace): void {
    if (!playSpace || !playSpace.id || !this.currentPlayer || this.creatingMatchAtLocationId) {
      return;
    }

    this.creatingMatchAtLocationId = playSpace.id;
    this.loading = true;
    this.spinner.show();

    const newEvent = new Event();
    newEvent.name = this.translate.instant("events.newTeam");
    newEvent.originalEventId = "";
    newEvent.manager = this.currentPlayer;
    newEvent.active = true;
    newEvent.openToPublic = false;
    newEvent.playSpace = Object.assign(new PlaySpace(), playSpace);
    newEvent.playSpace.location = null;
    newEvent.locationName = playSpace.name || null;
    newEvent.locationId = playSpace.id;
    newEvent.nextOccurrenceTimestamp = null;
    newEvent.nextOccurrenceTimestampZone = null;
    newEvent.playerEventInfos = Array<PlayerEventInfo>();
    newEvent.confirmedCount = 1;
    newEvent.maxPlayers = 10;
    newEvent.recurringType = new EventRecurringType().ONE_OFF;
    newEvent.incompleteSetup = true;

    this.eventService.createEvent(newEvent).subscribe(
      (event) => {
        event.originalEventId = event.originalEventId ? event.originalEventId : event.id;
        event.viewedByManager = true;
        event.playerEventInfos = event.playerEventInfos || [];
        localStorage.setItem("event", JSON.stringify(event));
        this.spinner.hide();
        this.loading = false;
        this.creatingMatchAtLocationId = null;
        this.router.navigate(["/event-players", false]);
      },
      (error) => {
        this.dialogService.alert({
          title: this.translate.instant('common.alert'),
          text: this.getBackendErrorMessage(error, this.translate.instant("locations.matchCreateFailed"))
        });
        this.spinner.hide();
        this.loading = false;
        this.creatingMatchAtLocationId = null;
      }
    );
  }

  public hasManagedDeleteStatusMatches(): boolean {
    return !!(this.deleteStatus && this.deleteStatus.managedMatches && this.deleteStatus.managedMatches.length > 0);
  }

  private loadPlaySpaceSummary(playSpace: PlaySpace): void {
    if (!playSpace || !playSpace.id) {
      this.playSpaceSummary = null;
      return;
    }

    this.playSpaceSummaryLoading = true;
    this.playSpaceService.getSummary(playSpace.id).subscribe(
      (summary) => {
        this.playSpaceSummary = summary;
        this.playSpaceSummaryLoading = false;
      },
      () => {
        this.playSpaceSummary = null;
        this.playSpaceSummaryLoading = false;
      }
    );
  }

  public openSelectedInMaps(playSpace: PlaySpace): void {
    const coordinates = this.playSpaceCoordinates(playSpace);
    const query = coordinates ? `${coordinates.latitude},${coordinates.longitude}` : this.locationLine(playSpace);
    if (!query) {
      return;
    }
    window.open("https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(query), "_blank");
  }

  public locationLine(playSpace: PlaySpace): string {
    if (!playSpace) {
      return "";
    }
    return this.hasText(playSpace.address) ? playSpace.address.trim() : "";
  }

  public addressLine(playSpace: PlaySpace): string {
    if (!playSpace) {
      return "";
    }
    return this.hasText(playSpace.address) ? playSpace.address.trim() : "";
  }

  public distanceLabel(playSpace: PlaySpace): string {
    if (!this.currentPlayerHasAddress()) {
      return "";
    }

    const currentCoordinates = this.currentPlayerCoordinates();
    const playSpaceCoordinates = this.playSpaceCoordinates(playSpace);
    if (!currentCoordinates || !playSpaceCoordinates) {
      return "";
    }

    const distance = this.distanceInKm(currentCoordinates.latitude, currentCoordinates.longitude, playSpaceCoordinates.latitude, playSpaceCoordinates.longitude);
    if (distance < 1) {
      return `${Math.round(distance * 1000)} m`;
    }
    return `${distance.toFixed(distance < 10 ? 1 : 0)} km`;
  }

  public hasCurrentPlayerLocation(): boolean {
    return !!this.currentPlayerCoordinates();
  }

  public setProfileCity(): void {
    this.router.navigate(["/player-detail", false], {queryParams: {section: "location", returnTo: "playspace"}});
  }

  public canLoadLocations(): boolean {
    return this.currentPlayerHasCity() && !!this.currentPlayerCoordinates();
  }

  private currentPlayerHasAddress(): boolean {
    return !!(this.currentPlayer && this.currentPlayer.address && this.currentPlayer.address.trim());
  }

  private currentPlayerHasCity(): boolean {
    return !!(this.currentPlayer && this.currentPlayer.city && this.currentPlayer.city.trim());
  }

  private preparePlaySpaceForMap(playSpace: PlaySpace): void {
    const coordinates = this.playSpaceCoordinates(playSpace);
    if (coordinates) {
      playSpace.latitude = coordinates.latitude;
      playSpace.longitude = coordinates.longitude;
      playSpace.googleLatLng = new google.maps.LatLng(coordinates.latitude, coordinates.longitude);
    }
  }

  private currentPlayerCoordinates(): {latitude: number, longitude: number} | null {
    const coordinates = this.currentPlayer && this.currentPlayer.location && this.currentPlayer.location.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) {
      return null;
    }
    const latitude = Number(coordinates[0]);
    const longitude = Number(coordinates[1]);
    if (!isFinite(latitude) || !isFinite(longitude)) {
      return null;
    }
    return { latitude, longitude };
  }

  private playSpaceCoordinates(playSpace: PlaySpace): {latitude: number, longitude: number} | null {
    if (!playSpace) {
      return null;
    }

    const latitude = Number(playSpace.latitude);
    const longitude = Number(playSpace.longitude);
    if (isFinite(latitude) && isFinite(longitude)) {
      return { latitude, longitude };
    }

    const coordinates = playSpace.location && playSpace.location.coordinates;
    if (Array.isArray(coordinates) && coordinates.length >= 2) {
      const geoLatitude = Number(coordinates[0]);
      const geoLongitude = Number(coordinates[1]);
      if (isFinite(geoLatitude) && isFinite(geoLongitude)) {
        return { latitude: geoLatitude, longitude: geoLongitude };
      }
    }

    return null;
  }

  private distanceInKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const earthRadiusKm = 6371;
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusKm * c;
  }

  private toRadians(degrees: number): number {
    return degrees * Math.PI / 180;
  }
}
